/*
Risk Causes Service

This service handles all API calls for risk causes.
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

const apiService = apiClient;

export interface RiskCause {
  id: number;
  entity_id: number;
  cause_name: string;
  description?: string;
  is_active: number;
  is_approved: number;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
  code_seq?: number;
  code?: string;
}

export interface CauseQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class RiskCausesService {
  async getAll(params?: CauseQueryParams): Promise<RiskCause[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const queryString = queryParams.toString();
      const url = `/api/v1/rsk/causes${queryString ? `?${queryString}` : ''}`;

      const response: AxiosResponse<ApiResponse<RiskCause[]>> = await apiService.get(url);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch risk causes');
      }
    } catch (error: any) {
      console.error('Error fetching risk causes:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /** Approved + active causes only, for link/select dropdowns. */
  async getDropdown(): Promise<{ id: number; name: string }[]> {
    const response = await apiService.get('/api/v1/rsk/causes/dropdown');
    return response.data?.items ?? [];
  }

  async getById(causeId: number): Promise<RiskCause> {
    try {
      const response: AxiosResponse<ApiResponse<RiskCause>> =
        await apiService.get(`/api/v1/rsk/causes/${causeId}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch risk cause');
      }
    } catch (error: any) {
      console.error(`Error fetching risk cause ${causeId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async create(causeData: Partial<RiskCause>): Promise<RiskCause> {
    try {
      const response: AxiosResponse<ApiResponse<RiskCause>> =
        await apiService.post('/api/v1/rsk/causes', causeData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create risk cause');
      }
    } catch (error: any) {
      console.error('Error creating risk cause:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async update(causeId: number, updateData: Partial<RiskCause>): Promise<RiskCause> {
    try {
      const response: AxiosResponse<ApiResponse<RiskCause>> =
        await apiService.put(`/api/v1/rsk/causes/${causeId}`, updateData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update risk cause');
      }
    } catch (error: any) {
      console.error(`Error updating risk cause ${causeId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async delete(causeId: number): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> =
        await apiService.delete(`/api/v1/rsk/causes/${causeId}`);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete risk cause');
      }
    } catch (error: any) {
      console.error(`Error deleting risk cause ${causeId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async toggleStatus(causeId: number): Promise<RiskCause> {
    try {
      const response: AxiosResponse<ApiResponse<RiskCause>> =
        await apiService.patch(`/api/v1/rsk/causes/${causeId}/toggle`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to toggle cause status');
      }
    } catch (error: any) {
      console.error(`Error toggling risk cause ${causeId} status:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async submit(id: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/causes/${id}/submit`);
    if (!res.data.success) throw new Error(res.data.error || 'Submit failed');
  }
  async approve(id: number, note?: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/causes/${id}/approve`, { note });
    if (!res.data.success) throw new Error(res.data.error || 'Approve failed');
  }
  async reject(id: number, reason: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/causes/${id}/reject`, { reason });
    if (!res.data.success) throw new Error(res.data.error || 'Reject failed');
  }
  async reopen(id: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/causes/${id}/reopen`);
    if (!res.data.success) throw new Error(res.data.error || 'Reopen failed');
  }

  async getHistory(id: number): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> {
    const response = await apiService.get(`/api/v1/rsk/causes/${id}/history`);
    const rows = response.data?.data ?? response.data?.items ?? [];
    return (rows as any[]).map((r: any) => ({
      audit_id: r.id,
      action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
      changed_by_username: r.changed_by_username || 'Unknown',
      changed_on: r.changed_on,
      change_reason: r.remarks,
    }));
  }
}

const riskCausesService = new RiskCausesService();
export default riskCausesService;
